import type * as THREE from "three";
import { buildCowryGeometry } from "./geometry/buildCowryGeometry";
import { buildLathePropulsionOrgan } from "./geometry/buildLathePropulsionOrgan";
import { buildSpindleOrgan } from "./geometry/buildSpindleOrgan";
import { buildCowryMaterial } from "./materials/buildCowryMaterial";
import { generateTextureSet } from "./textures/generateTextureSet";
import type { CollectionId, CollectionConfig, CowryTraits, ItemSeed } from "./core/types";
import { deriveCowryTraits } from "./traits/deriveCowryTraits";
import { getCollectionConfig } from "./collections/registry";

export interface BuildKowryRequest {
  collectionId: CollectionId;
  itemSeed: ItemSeed;
  textureResolution?: number;
}

export interface BuiltKowry {
  root: THREE.Group;
  traits: CowryTraits;
  collection: CollectionConfig;
  textureResolution: number;
}

export function buildKowry(request: BuildKowryRequest): BuiltKowry {
  validateItemSeed(request.itemSeed);
  const collection = getCollectionConfig(request.collectionId);
  const textureResolution = request.textureResolution ?? collection.textureResolution;
  const traits = deriveCowryTraits(collection, request.itemSeed);
  const cowry = buildCowryGeometry(traits.geometry, {
    ventralFoldEmphasis: collection.ventralTreatment.foldEmphasis,
  });
  const textures = generateTextureSet(collection, traits, request.itemSeed, {
    width: textureResolution,
    height: textureResolution,
    ventralMaterialEmphasis: collection.ventralTreatment.materialEmphasis,
    ventralIvory: true,
  });
  cowry.body.material.dispose();
  cowry.body.material = buildCowryMaterial(traits, textures);
  const lathe = buildLathePropulsionOrgan(
    traits.geometry,
    request.itemSeed,
    collection.ventralTreatment.foldEmphasis,
    collection.lathePropulsion,
  );
  const spindle = buildSpindleOrgan(
    traits.geometry,
    request.itemSeed,
    collection.ventralTreatment.foldEmphasis,
  );
  cowry.root.add(lathe.root, spindle.root);
  return {
    root: cowry.root,
    traits,
    collection,
    textureResolution,
  };
}

function validateItemSeed(itemSeed: string): void {
  if (!/^[0-9a-f]{64}$/i.test(itemSeed)) {
    throw new Error("Kowry item seeds must be 64 hexadecimal characters.");
  }
}